import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaLock } from "react-icons/fa";
import { MdLaptopChromebook } from "react-icons/md";
import { FaBuilding } from "react-icons/fa";
import { translate } from "../../../../utils/Common";
import "../door-badge.css";


const LockerDoor = ({
  index,
  item,
  column1,
  column2,
  selectedDoor,
  showPackageInfo,
  setshowPackageInfo,
  activeSearch,
  onLockerClick,
  renderValidateInfoPack, 
  styles,
  doorAssignments,
}) => {
  const doorHeight = 540;

  const doorRef = useRef(null);
  const tooltipRef = useRef(null);
  const closeTimerRef = useRef(null);
  // `null` = todavía sin medir; el tooltip se monta invisible para poder medirlo.
  const [tooltipPos, setTooltipPos] = useState(null);

  const hasPackages = item?.isAvailable === false && item?.packages?.length > 0;
  const isTooltipOpen = showPackageInfo === item.boxitDoorId && hasPackages;

  // El tooltip vivía dentro de la puerta como `position: absolute`, así que lo
  // recortaba el `overflow-x` de `.section-smart-locker` (y lo encogía el
  // `scale()` de `.doorsContainer`), que es de dónde venían el corte y el
  // scroll. Ahora se pinta en un portal sobre <body> con posición fija: crece
  // con su contenido y sólo se reubica para no salirse del viewport.
  const placeTooltip = useCallback(() => {
    const door = doorRef.current;
    const tooltip = tooltipRef.current;
    if (!door || !tooltip) return;

    const margin = 8;
    const doorRect = door.getBoundingClientRect();
    const tipRect = tooltip.getBoundingClientRect();

    let left = doorRect.right + margin;
    if (left + tipRect.width > window.innerWidth - margin) {
      left = doorRect.left - tipRect.width - margin;
    }
    if (left < margin) {
      left = Math.max(margin, (window.innerWidth - tipRect.width) / 2);
    }

    let top = doorRect.top;
    if (top + tipRect.height > window.innerHeight - margin) {
      top = window.innerHeight - tipRect.height - margin;
    }
    if (top < margin) top = margin;

    setTooltipPos((prev) =>
      prev && prev.top === top && prev.left === left ? prev : { top, left }
    );
  }, []);

  useLayoutEffect(() => {
    if (!isTooltipOpen) {
      setTooltipPos(null);
      return undefined;
    }

    placeTooltip();
    // `true` para capturar también el scroll de los contenedores internos.
    window.addEventListener("scroll", placeTooltip, true);
    window.addEventListener("resize", placeTooltip);
    return () => {
      window.removeEventListener("scroll", placeTooltip, true);
      window.removeEventListener("resize", placeTooltip);
    };
  }, [isTooltipOpen, placeTooltip]);

  // El tooltip ya no es hijo de la puerta (vive en el portal), así que al pasar
  // el mouse de una a otro se dispara el mouseleave de la puerta. Un cierre
  // diferido deja cruzar ese hueco y poder pulsar el botón de copiar.
  const openTooltip = useCallback(() => {
    clearTimeout(closeTimerRef.current);
    setshowPackageInfo(item.boxitDoorId);
  }, [item.boxitDoorId, setshowPackageInfo]);

  const closeTooltip = useCallback(() => {
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      // Sólo cierra si sigue siendo esta puerta: si el mouse ya entró en otra,
      // no debe apagar su tooltip.
      setshowPackageInfo((prev) => (prev === item.boxitDoorId ? null : prev));
    }, 150);
  }, [item.boxitDoorId, setshowPackageInfo]);

  useLayoutEffect(() => () => clearTimeout(closeTimerRef.current), []);

  const handleDoorStatus = (door) => {
    let status = {
      shadowColor: "#FFFFFF",
      shadowOffset: { width: 0, height: 3 },
      shadowOpacity: 0.1,
      shadowRadius: 7,
      border: "1px solid",
      borderRadius: "12px",
      color: "white",
      fontSize: "12px",
      padding: "8px 12px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)",
      transition: "all 0.2s ease-in-out",
    };
    if (door?.isActive) {
      if (door?.isAvailable) return { 
        ...status, 
        backgroundColor: "#68A46A",
        borderColor: "#5a8a5c"
      };
      if (door?.isAvailable === false) {
        switch (door?.packageStatusId) {
          case 0: return { 
            ...status, 
            backgroundColor: (!door.packages || door.packages.length === 0) ? "#ea580c" : "#3AA0E8",
            borderColor: (!door.packages || door.packages.length === 0) ? "#d14a0a" : "#2a8bd1"
          }; // RESERVED
          case 1: return { 
            ...status, 
            backgroundColor: "#E5BA00",
            borderColor: "#d1a300"
          }; // ON BOXIT
          case 2: return { 
            ...status, 
            backgroundColor: "#828282",
            borderColor: "#6a6a6a"
          }; // EXPIRED
          default:
            break;
        }

      }
    }
    //   else return { ...status, backgroundColor: "#E5BA00" };
    else return { 
      ...status, 
      backgroundColor: "#393939",
      borderColor: "#2a2a2a",
      opacity: 0.7
    }; // INACTIVE
  };
    const getSize = (size, columnCount) => {
    let height;
    switch (size) {
      case "S":
        height = doorHeight / columnCount + "%"; // Porcentaje basado en la cantidad de columnas
        break;
      case "M":
        height = doorHeight / columnCount + "%";
        break;
      case "L":
        height = doorHeight / columnCount + "%";
        break;
      case "XL":
        height = doorHeight / columnCount + "%";
        break;
      default:
        height = doorHeight / columnCount + "%";
    }
    return { height };
  };

  return (
    <div
      key={"door-smart-" + index}
      ref={doorRef}
      style={{
        ...getSize(
          item.dimension,
          item?.columnModule === 1 ? column1?.length : column2?.length
        ),
        cursor: "pointer",
        ...handleDoorStatus(item),
        ...(selectedDoor?.includes(item.boxitDoorId) && { opacity: 0.5 }),
      }}
      onClick={() => onLockerClick(item?.boxitDoorId)}
      className={`p-2 rounded-md locker-door ${activeSearch ? "disable-locker-click" : ""}`}
      onMouseEnter={openTooltip}
      onMouseLeave={closeTooltip}
    >
      <div style={styles.doorStatus} className="section-smart-locker-door">
        {/* The number and its badges used to be plain inline spans, so in a
            narrow column they wrapped and the lock and screen icons landed on
            top of each other. A flex row with a gap keeps them on one line and
            lets them shrink instead of stacking. */}
        <div className="flex min-w-0 items-start gap-1.5 self-start leading-none">
          <span>{item.doorNumber}</span>
          {!item?.isActive && (
            <span className="door-badge" style={{ background: "rgba(255,255,255,0.22)" }} title={translate("global.inactive")}>
              <FaLock size={11} />
            </span>
          )}
          {item?.isScreen && (
            <span className="door-badge" style={{ background: "#0058ff" }} title="Screen">
              <MdLaptopChromebook size={13} />
            </span>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
          <div>
            {item?.isScreen ? "C - " : item?.dimension && `${item.dimension} - `}
            {item?.layer && `${item.layer}`}
          </div>
          {doorAssignments[item.boxitDoorId]?.companyName && (
            <div
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                padding: "2px 6px",
                borderRadius: "12px",
                fontSize: "8px",
                color: "white",
                fontWeight: "600",
                letterSpacing: "0.2px",
                textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                boxShadow: "0 2px 4px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)",
                border: "1px solid rgba(255,255,255,0.3)",
                backdropFilter: "blur(12px)",
                transition: "all 0.3s ease-in-out",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "3px",
                minWidth: "fit-content",
                maxWidth: "80px",
                marginTop: "4px",
                zIndex: 10
              }}
              title={`${translate("global.business")}: ${doorAssignments[item.boxitDoorId].companyName}`}
            >
              <FaBuilding size={7} style={{ opacity: 0.95 }} />
              <span>
                {doorAssignments[item.boxitDoorId].companyName.length > 8 
                  ? doorAssignments[item.boxitDoorId].companyName.substring(0, 8) + "..." 
                  : doorAssignments[item.boxitDoorId].companyName}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Mostrar información de los paquetes usando renderValidateInfoPack */}
      {isTooltipOpen &&
        createPortal(
          <div
            ref={tooltipRef}
            className="package-info-tooltip"
            style={{
              top: tooltipPos?.top ?? 0,
              left: tooltipPos?.left ?? 0,
              visibility: tooltipPos ? "visible" : "hidden",
            }}
            onMouseEnter={openTooltip}
            onMouseLeave={closeTooltip}
            // Un portal de React sigue propagando los eventos por el árbol de
            // React, no por el del DOM, así que sin esto cualquier click dentro
            // del popover (los botones de copiar) acaba en el onClick de la
            // puerta y la selecciona.
            onClick={(e) => e.stopPropagation()}
          >
            {item.packages.map((pkg, pkgIndex) => (
              <div key={`packages-${pkgIndex}`} className={`${(item.packages.length > 1 && item.packages.length !== (pkgIndex + 1)) ? 'mb-2 border-b border-border pb-2' : ''}`}>
                {renderValidateInfoPack(pkg)}
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}

export default LockerDoor;